/* AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { DATE_NATIVE_FORMAT, ReportStatusEnum } from "../../constant";
import { IPatientResponse } from "../../types/patient";
import { IDiagnosticReport } from "../../types/studies";
import { getHumanName, getRenderKey } from "../../utils/format";
import { formatDate, parseDateFromString } from "../../utils/utils";
import { PatientId } from "../Patient/PatientId";
import { PatientName } from "../Patient/PatientName";
import { formatValidDate } from "../../helpers";

interface IDiagnosticReportHeader {
  studyDate?: Date | string;
  accessionNumber?: string;
  patientDetail?: IPatientResponse;
  dateTimeFormat: string;
  dateFormat: string;
  reportDetail?: IDiagnosticReport;
  issuerEnabled?: boolean;
  modalities?: Array<string>;
}

export const DiagnosticReportHeader: FC<IDiagnosticReportHeader> = ({
  studyDate,
  patientDetail,
  dateTimeFormat,
  reportDetail,
  dateFormat,
  accessionNumber,
  issuerEnabled,
  modalities,
}) => {
  const { t } = useTranslation("modal");
  return (
    <div className="form-group row m-b-xs">
      <div className="col-lg-4">
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("patient_name")}:
          </div>
          <div className="break-word flex-auto ">
            <PatientName
              patient={patientDetail}
              showIcon={false}
              dateFormat={dateFormat}
            />
          </div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("patient_id")}:
          </div>
          <div className="break-word flex-auto ">
            <PatientId
              issuerEnabled={issuerEnabled}
              patientIds={patientDetail?.patientIds || []}
            />
          </div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("dob")}:
          </div>
          <div className="break-word flex-auto ">
            {formatDate(
              parseDateFromString(patientDetail.dob, DATE_NATIVE_FORMAT),
              dateFormat
            )}
          </div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("general.sex")}:
          </div>
          <div className="break-word flex-auto ">
            {patientDetail?.sex ? t(getRenderKey(patientDetail?.sex)) : ""}
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("accession_number")}:
          </div>
          <div className="break-word flex-auto ">{accessionNumber}</div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("study_date")}:
          </div>
          <div className="break-word flex-auto ">
            {studyDate ? formatValidDate(studyDate, dateTimeFormat) : ""}
          </div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("modality")}:
          </div>
          <div className="break-word flex-auto ">{modalities?.join(", ")}</div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("referrer")}:
          </div>
          <div className="break-word flex-auto ">
            {getHumanName(reportDetail?.referrer)}
          </div>
        </div>
      </div>

      <div className="col-lg-4">
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("issued_at")}:
          </div>
          <div className="break-word flex-auto ">
            {reportDetail?.issuedAt &&
              formatValidDate(reportDetail?.issuedAt, dateTimeFormat)}
          </div>
        </div>
        <div className="flexbox m-b-xxs">
          <div className="message_lable_detail p-r-xs font-weight-bold">
            {t("modal:diagnostic_report.report_status")}:
          </div>
          <div className="break-word flex-auto ">
            {reportDetail?.status
              ? t(`diagnostic_report.${ReportStatusEnum[reportDetail.status]}`)
              : ""}
          </div>
        </div>
      </div>
    </div>
  );
};
